import { type FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { type NumberFormatValues } from 'react-number-format';

import { useCalcComiss, useTranslate, useValidate } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setAmount } from 'store/payment/slice';
import { Input } from 'views/components/base/ui';

type AmountFormPropTypes = {
  label?: string;
  suffix?: string;
  isMobile?: boolean;
};

const AmountForm: FC<AmountFormPropTypes> = ({ label, suffix, isMobile }) => {
  const { amount, paymentData } = useAppSelector((state) => state.payment);
  const { t } = useTranslate();
  const { control, setValue } = useForm({
    mode: 'onChange',
  });
  useCalcComiss();
  const { validateAmount } = useValidate();
  const dispatch = useAppDispatch();

  const { maxAmount, minAmount } = useMemo(
    () => ({
      maxAmount: Number(paymentData?.maxAmount),
      minAmount: Number(paymentData?.minAmount),
    }),
    [paymentData],
  );

  const handleChangeAmount = (value: NumberFormatValues) => {
    if (paymentData) {
      setValue('amount', value.value, { shouldValidate: true });
      dispatch(setAmount(value.value));
    }
  };

  return paymentData ? (
    <form>
      <Controller
        name="amount"
        control={control}
        defaultValue="0"
        rules={{
          required: 'Сумма обязательна к заполнению',
          validate: (value) => validateAmount(value, maxAmount, minAmount),
        }}
        render={({ fieldState: { error } }) => (
          <Input
            onValueChange={handleChangeAmount}
            name="amount"
            value={amount}
            label={label}
            upperLabel
            isMobile={isMobile}
            containerClassName="text-right md:text-left"
            inputClassName="text-right md:text-left max-w-[257px] md:max-w-none placeholder:font-medium md:placeholder:text-[18px]"
            placeholder={t('enter_amount')}
            required
            amount
            suffix={suffix}
            error={error?.message}
          />
        )}
      />
    </form>
  ) : null;
};

export { AmountForm };
